import Dashboard from '@pages/Dashboard';
import CrudPage from '@pages/CrudPage';
import { merchantRoutes } from '@/merchant/routes';

// Friendlier sidebar labels for generic CRUD pages (falls back to the model name).
const LABELS = { User: 'Users', Role: 'Roles', Permission: 'Permissions', ApiToken: 'API tokens', Category: 'Categories' };

const kebab = (s) =>
  String(s)
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();

/**
 * The merchant back-office route table: hand-built screens from
 * merchant/routes.jsx, then a generic CRUD page (sidebar group "Data") for
 * every registry model those screens don't replace (pass the live registry from
 * useModules()). Nav items + route guards key off (module, action).
 * `store` (the merchant resolved from the host) is passed to every screen.
 */
export function buildRoutes(modules = {}, { store } = {}) {
  // A screen whose module the store's registry leaves out (a capability module, D57) is not routed.
  const custom = merchantRoutes
    .filter((r) => !r.module || modules[r.module])
    .map((r) => ({ nav: true, action: 'view', ...r, element: <r.Element store={store} /> }));
  const replaced = new Set(custom.flatMap((r) => r.models || []));
  const routes = custom.some((r) => r.path === '/')
    ? custom
    : [{ path: '/', element: <Dashboard />, label: 'Dashboard', icon: 'layout-dashboard', module: 'core', action: 'view', nav: true, group: 'Overview' }, ...custom];

  for (const mod of Object.values(modules)) {
    // `crud: false` modules declare permissions only; their screens are hand-built.
    if (mod.crud === false) continue;
    for (const model of mod.models || []) {
      if (replaced.has(model)) continue;
      routes.push({
        path: `/${kebab(model)}`,
        element: <CrudPage model={model} module={mod.key} title={LABELS[model] || model} />,
        label: LABELS[model] || model,
        icon: mod.icon,
        module: mod.key,
        action: 'view',
        nav: true,
        group: 'Data',
      });
    }
  }
  return routes;
}
